import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { NavIcon, type NavIconName } from '@/components/icons';

interface NavItem {
  path: string;
  labelKey: string;
  icon: NavIconName;
  matchPaths?: string[];
  staffOnly?: boolean;
}

const navItems: NavItem[] = [
  {
    path: '/',
    labelKey: 'nav.dashboard',
    icon: 'nav-dashboard',
  },
  {
    path: '/active-calls',
    labelKey: 'nav.activeCalls',
    icon: 'nav-active-calls',
    staffOnly: true,
  },
  {
    path: '/call-history',
    labelKey: 'nav.callHistory',
    icon: 'nav-call-history',
  },
  {
    path: '/voicemails',
    labelKey: 'nav.voicemails',
    icon: 'nav-voicemails',
  },
  {
    path: '/address-book',
    labelKey: 'nav.addressBook',
    icon: 'nav-address-book',
  },
  {
    path: '/users',
    labelKey: 'nav.usersManagement',
    icon: 'nav-users',
    matchPaths: ['/users', '/nominals'],
  },
  {
    path: '/keywords',
    labelKey: 'nav.keywordMonitoring',
    icon: 'nav-keywords',
  },
  {
    path: '/messages/pending',
    labelKey: 'nav.pendingMessages',
    icon: 'nav-messages',
    staffOnly: true,
  },
];

function isNavActive(pathname: string, item: NavItem): boolean {
  if (pathname === item.path) {
    return true;
  }
  return item.matchPaths?.includes(pathname) ?? false;
}

const navLabelClassName =
  'flex min-h-12 max-w-[6.5rem] flex-col justify-center text-base leading-6';

type SidebarProps = {
  isStaff: boolean;
};

export const Sidebar: React.FC<SidebarProps> = ({ isStaff }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebar-collapsed');
    if (stored !== null) {
      return stored === 'true';
    }
    return window.innerWidth < 1280;
  });

  useEffect(() => {
    const handleResize = () => {
      if (localStorage.getItem('sidebar-collapsed') === null) {
        setCollapsed(window.innerWidth < 1280);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  return (
    <aside
      className={clsx(
        'flex min-h-0 shrink-0 flex-col bg-white transition-all duration-200 ease-in-out',
        collapsed ? 'w-12 items-center sm:w-14' : 'w-[180px] xl:w-[206px]'
      )}
    >
      <div
        className={clsx(
          'flex w-full pt-2',
          collapsed ? 'justify-center' : 'justify-end px-2'
        )}
      >
        <button
          type="button"
          onClick={toggle}
          className="flex size-5 items-center justify-center rounded hover:bg-white/60"
          title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        >
          <NavIcon name="sidebar-collapse" className="size-5 text-unilink-text-primary" />
        </button>
      </div>

      <nav
        className={clsx(
          'min-h-0 w-full flex-1 space-y-2 overflow-y-auto pb-4',
          collapsed ? 'flex flex-col items-center pt-[15px]' : 'px-2 pt-[15px]'
        )}
      >
        {navItems.filter(item => !item.staffOnly || isStaff).map(item => {
          const isActive = isNavActive(location.pathname, item);
          return (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? t(item.labelKey) : undefined}
              className={clsx(
                'flex items-center transition-colors',
                collapsed
                  ? 'size-10 shrink-0 justify-center rounded-full p-0'
                  : 'w-full gap-3 rounded-full px-4 py-2',
                isActive
                  ? 'bg-unilink-surface-nav-active font-semibold text-unilink-text-primary'
                  : 'font-normal text-unilink-text-primary hover:bg-white/70'
              )}
            >
              <NavIcon
                name={item.icon}
                filled={isActive}
                className="size-6 text-blue-navbar"
              />
              {!collapsed && (
                <span className={navLabelClassName}>{t(item.labelKey)}</span>
              )}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
};
